import { FC } from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { CustomDrawer } from "../../../atoms/custom-drawer/custom-drawer";
import { useAppSelector } from "../../../../hooks";
import { PricingResponse } from "../../../../typings/interfaces/responses/pricing-response";
import { Colors } from "../../../../configs";
import { CartItem } from "../../../../typings/interfaces/pos/cart-item";

interface IDebugDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
}

const DebugDrawer: FC<IDebugDrawerProps> = ({ isOpen, toggleDrawer }) => {
  const { selectedSession, selectedServiceType } = useAppSelector(
    (state) => state.cart
  );
  const { dryCleanResponse } = useAppSelector((state) => state.pricing);

  const cartItems = selectedSession?.cartItems?.filter((item) => item.finalized) || [];

  const getItemIds = (item: CartItem) => {
    const categoryId =
      selectedServiceType?.id === -100000
        ? item.serviceType?.id
        : selectedServiceType?.id;

    return {
      categoryId: categoryId || 0,
      categoryName: selectedServiceType?.categoryName || item.serviceType?.categoryName || "N/A",
      subCategoryId: item.garmentType?.id || 0,
      subCategoryName: item.garmentType?.name || "N/A",
      secondSubCategoryId: item.garmentStyle?.id || 0,
      secondSubCategoryName: item.garmentStyle?.name || "N/A",
      materialId: item.materialType?.id || 0,
      materialName: item.materialType?.name || "N/A",
    };
  };

  const findMatchedPrice = (item: CartItem) => {
    const ids = getItemIds(item);
    
    const matchedPrices = dryCleanResponse?.body?.filter(
      (price: PricingResponse) => {
        return (
          price.categoryId === ids.categoryId &&
          price.subCategoryId === ids.subCategoryId &&
          price.secondSubCategoryId === ids.secondSubCategoryId &&
          price.materialId === ids.materialId
        );
      }
    ) || [];

    return {
      found: matchedPrices.length > 0,
      price: matchedPrices[0]?.price || 0,
      actualPrice: item.unitPrice || 0,
    };
  };

  return (
    <CustomDrawer isOpen={isOpen} toggleDrawer={toggleDrawer} sx={{ width: 600 }}>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            🔍 Cart Items Debug Panel
          </Typography>
          <IconButton onClick={toggleDrawer} size="small">
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Summary */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: Colors.BACKGROUND_GRAY }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Summary
          </Typography>
          <Typography variant="body2">
            Total items in cart: {cartItems.length}
          </Typography>
          <Typography variant="body2">
            Total prices loaded: {dryCleanResponse?.body?.length || 0}
          </Typography>
        </Paper>

        {/* Cart Items Table */}
        {cartItems.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 300px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Category ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Sub Cat ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    2nd Sub Cat ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Material ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Matched Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Actual Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: Colors.SECONDARY, color: "white" }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cartItems.map((item, index) => {
                  const ids = getItemIds(item);
                  const priceInfo = findMatchedPrice(item);
                  const priceMatch = priceInfo.found && priceInfo.price === priceInfo.actualPrice;

                  return (
                    <TableRow
                      key={item.itemId}
                      sx={{
                        bgcolor: priceMatch
                          ? "#e8f5e9"
                          : priceInfo.found
                          ? "#fff3e0"
                          : "#ffebee",
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ids.categoryId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ids.categoryName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ids.subCategoryId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ids.subCategoryName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ids.secondSubCategoryId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ids.secondSubCategoryName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {ids.materialId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ids.materialName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: priceInfo.found ? "green" : "red",
                          }}
                        >
                          {priceInfo.found ? `$${priceInfo.price.toFixed(2)}` : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          ${priceInfo.actualPrice.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {priceMatch ? (
                          <Typography variant="caption" sx={{ color: "green" }}>
                            ✅ Match
                          </Typography>
                        ) : priceInfo.found ? (
                          <Typography variant="caption" sx={{ color: "orange" }}>
                            ⚠️ Mismatch
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "red" }}>
                            ❌ Not Found
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No items in cart
            </Typography>
          </Paper>
        )}

        {/* Legend */}
        <Box sx={{ mt: 2, p: 2, bgcolor: Colors.BACKGROUND_GRAY, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: "bold", display: "block", mb: 1 }}>
            Legend:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: "#e8f5e9", borderRadius: 0.5 }} />
              <Typography variant="caption">Price matches</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: "#fff3e0", borderRadius: 0.5 }} />
              <Typography variant="caption">Price found but mismatch</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 20, height: 20, bgcolor: "#ffebee", borderRadius: 0.5 }} />
              <Typography variant="caption">Price not found</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </CustomDrawer>
  );
};

export default DebugDrawer;


